<template>
  <transition name="router-anim">
    <div class="kits sku-detail-page">
      <div class="sku-detail-container">
        <!-- TOP BAR: Left: Breadcrumb Trail (Inventory > Items > SKU Detail: <SKU>) | Right: ← Back to Inventory Button -->
        <div
          class="breadcrumb-top-bar flex-between align-center mb-16 flex-wrap gap-12"
        >
          <Breadcrumb :breadcrumbs="breadcrumbItems" />
          <button
            class="back-inventory-btn"
            @click="goBack"
            title="Back to Inventory Dashboard"
          >
            ← Back to Inventory
          </button>
        </div>

        <!-- OVERVIEW CARD WITH PROMINENT TOTAL QUANTITY & COMPACT DATE FILTER -->
        <div class="card-box overview-card">
          <!-- Top Subtitle Row: Heading on Left | Refresh Button on Right -->
          <div class="subtitle-row flex-between align-center flex-wrap gap-12">
            <span class="card-subtitle-gray"
              >SKU STOCK & EXPIRY DETAIL OVERVIEW</span
            >
            <button
              class="refresh-data-btn"
              @click="fetchStockDetails"
              :disabled="loading"
              title="Refresh Data"
            >
              <span class="refresh-icon" :class="{ spinning: loading }"
                >🔄</span
              >
              Refresh Data
            </button>
          </div>

          <!-- Main Controls Row -->
          <div class="controls-row flex-between align-center flex-wrap gap-16">
            <!-- Left: Combined SKU & Product Name Card + Prominent Total Quantity Badge -->
            <div class="sku-header-left flex-align-center gap-12 flex-wrap">
              <div class="combined-sku-item-card flex-align-center gap-10" title="Item Description & SKU">
                <span
                  v-if="displayItemDescription"
                  class="combined-item-desc"
                  :title="displayItemDescription"
                >
                  {{ displayItemDescription }}
                </span>
                <span class="sku-badge-pill" title="SKU Code">({{ currentSku }})</span>
              </div>
              <div
                class="prominent-total-qty-badge"
                title="Total Quantity across all records"
              >
                <span class="qty-badge-label">Total Quantity:</span>
                <span class="qty-badge-val">{{ totalQuantityFormatted }}</span>
              </div>
            </div>

            <!-- Right: Single Compact Date-Range Filter (Expiration Date From - To) -->
            <div class="compact-date-filter flex-align-center gap-8 flex-wrap">
              <span class="filter-icon-label">📅 Expiration Date:</span>
              <div class="date-input-chip">
                <span class="chip-label">From:</span>
                <input
                  type="date"
                  v-model="fromDateExpiry"
                  class="compact-date-input"
                  @change="applyFilters"
                  title="Expiration Date From"
                />
              </div>
              <span class="range-arrow">→</span>
              <div class="date-input-chip">
                <span class="chip-label">To:</span>
                <input
                  type="date"
                  v-model="toDateExpiry"
                  class="compact-date-input"
                  @change="applyFilters"
                  title="Expiration Date To"
                />
              </div>
              <button
                v-if="fromDateExpiry || toDateExpiry"
                class="clear-chip-btn"
                @click="clearFilters"
                title="Clear Date Filter"
              >
                ✕ Clear
              </button>
            </div>
          </div>
        </div>

        <!-- PRODUCT QUANTITY BREAKDOWN TABLE CARD -->
        <div class="card-box table-card">
          <div
            class="table-header-row flex-between align-center flex-wrap gap-12"
          >
            <div class="table-title-wrap flex-align-center gap-12">
              <h3 class="table-title">📋 Product Quantity Breakdown</h3>
              <span class="entries-badge"
                >{{ filteredRecords.length }} Entries</span
              >
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-box">
            <img
              src="../../images/bg/spinner.png"
              alt="Loading"
              class="spinner-img spinning"
            />
            <p class="loading-text">
              Loading SKU stock details for <strong>{{ displayItemDescription ? `${displayItemDescription} (${currentSku})` : currentSku }}</strong
              >...
            </p>
          </div>

          <!-- Table View (Styled 100% identically to InventoryItems.vue modern-table) -->
          <div v-else class="table-responsive">
            <table class="modern-table breakdown-table">
              <thead>
                <tr>
                  <th
                    style="width: 14%"
                    @click="sortBy('stock_id')"
                    class="sortable"
                    title="Click to sort by Stock ID"
                  >
                    Stock ID
                    <span class="sort-icon">{{ getSortIcon('stock_id') }}</span>
                  </th>
                  <th
                    style="width: 10%"
                    @click="sortBy('qty')"
                    class="sortable text-center"
                    title="Click to sort by Quantity"
                  >
                    Quantity
                    <span class="sort-icon">{{ getSortIcon('qty') }}</span>
                  </th>
                  <th
                    style="width: 10%"
                    @click="sortBy('unit')"
                    class="sortable text-center"
                    title="Click to sort by Unit"
                  >
                    Unit
                    <span class="sort-icon">{{ getSortIcon('unit') }}</span>
                  </th>
                  <th
                    style="width: 14%"
                    @click="sortBy('date_received')"
                    class="sortable"
                    title="Click to sort by Date Received"
                  >
                    Date Received
                    <span class="sort-icon">{{ getSortIcon('date_received') }}</span>
                  </th>
                  <th
                    style="width: 14%"
                    @click="sortBy('date_expiry')"
                    class="sortable"
                    title="Click to sort by Expiration Date"
                  >
                    Expiration Date
                    <span class="sort-icon">{{ getSortIcon('date_expiry') }}</span>
                  </th>
                  <th
                    style="width: 18%"
                    @click="sortBy('facility_zone')"
                    class="sortable"
                    title="Click to sort by Facility / Zone"
                  >
                    Facility / Zone
                    <span class="sort-icon">{{ getSortIcon('facility_zone') }}</span>
                  </th>
                  <th
                    style="width: 10%"
                    @click="sortBy('location')"
                    class="sortable"
                    title="Click to sort by Location"
                  >
                    Location
                    <span class="sort-icon">{{ getSortIcon('location') }}</span>
                  </th>
                  <th
                    style="width: 10%"
                    @click="sortBy('batch')"
                    class="sortable"
                    title="Click to sort by Batch"
                  >
                    Batch
                    <span class="sort-icon">{{ getSortIcon('batch') }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(record, index) in filteredRecords"
                  :key="index"
                  class="table-row"
                >
                  <td class="font-semibold">
                    <code class="clickable-sku-code">{{
                      getStockId(record)
                    }}</code>
                  </td>
                  <td class="text-center">
                    <span class="qty-item-badge">{{
                      getQty(record)
                    }}</span>
                  </td>
                  <td class="text-center">
                    <span class="unit-tag">{{
                      getUnit(record)
                    }}</span>
                  </td>
                  <td class="date-cell">
                    {{ formatDate(getDateReceived(record)) }}
                  </td>
                  <td
                    class="date-cell"
                    :class="{
                      'text-expiry': isExpiringSoon(getDateExpiry(record))
                    }"
                  >
                    {{ formatDate(getDateExpiry(record)) }}
                  </td>
                  <td>
                    <span class="location-tag">{{
                      getFacilityZone(record)
                    }}</span>
                  </td>
                  <td>
                    <span class="location-tag sub-loc">{{
                      getLocation(record)
                    }}</span>
                  </td>
                  <td>{{ getBatch(record) }}</td>
                </tr>

                <tr v-if="filteredRecords.length === 0">
                  <td colspan="8" class="no-records-cell text-center">
                    No stock records found matching current criteria for SKU
                    <strong>"{{ currentSku }}"</strong>.
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- UNIT QUANTITY BREAKDOWN SUMMARY (EACH UNIT SEPARATELY IN DIFFERENT COLORS) -->
            <div
              v-if="unitTotalsArray.length > 0"
              class="unit-breakdown-summary mt-20"
            >
              <h4 class="unit-summary-title mb-12">
                📊 Unit Quantity Subtotals
              </h4>
              <div
                class="unit-badges-container flex-align-center gap-12 flex-wrap"
              >
                <div
                  v-for="(item, idx) in unitTotalsArray"
                  :key="'unit-total-' + idx"
                  class="unit-total-chip"
                  :style="{
                    backgroundColor: item.theme.bg,
                    color: item.theme.color,
                    borderColor: item.theme.border
                  }"
                >
                  <span class="unit-chip-name">{{ item.unit }}:</span>
                  <strong class="unit-chip-val">{{ item.formattedQty }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from "axios";
import moment from "moment";
import Breadcrumb from "./breadcrumb/Breadcrumb";

export default {
  name: "SkuDetail",
  components: {
    Breadcrumb
  },
  props: {
    sku: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loading: true,
      records: [],
      fetchedItemDescription: "",
      fromDateExpiry: "",
      toDateExpiry: "",
      isGoingBack: false,
      sortKey: "",
      sortDirection: "asc"
    };
  },
  computed: {
    currentSku() {
      return (
        this.sku ||
        (this.$route && this.$route.params ? this.$route.params.sku : "") ||
        ""
      );
    },
    displayItemDescription() {
      if (this.fetchedItemDescription) {
        return this.fetchedItemDescription;
      }
      if (this.$route && this.$route.query && this.$route.query.description) {
        return this.$route.query.description;
      }
      if (
        this.$route &&
        this.$route.params &&
        this.$route.params.item_description
      ) {
        return this.$route.params.item_description;
      }
      if (Array.isArray(this.records) && this.records.length > 0) {
        for (const rec of this.records) {
          if (rec && typeof rec === "object") {
            const d =
              rec.item_description ||
              rec.ItemDescription ||
              rec.Description ||
              rec.item_desc ||
              rec.description;
            if (d) return d;
          }
        }
      }
      return "";
    },
    breadcrumbItems() {
      const labelText = this.displayItemDescription
        ? `${this.displayItemDescription} (${this.currentSku})`
        : `SKU Detail: ${this.currentSku}`;
      return [
        { text: "Inventory", link: "/dashboard" },
        { text: "Items", link: "/dashboard" },
        { text: labelText }
      ];
    },
    totalQuantity() {
      if (!Array.isArray(this.filteredRecords)) return 0;
      return this.filteredRecords.reduce(
        (sum, item) => sum + parseInt(this.getQty(item) || 0, 10),
        0
      );
    },
    totalQuantityFormatted() {
      return this.totalQuantity.toLocaleString();
    },
    unitTotalsArray() {
      if (!Array.isArray(this.filteredRecords)) return [];

      const map = new Map();
      for (const item of this.filteredRecords) {
        const qty = parseInt(this.getQty(item) || 0, 10);
        let unit = "";
        if (item && typeof item === "object") {
          unit =
            item.Unit ||
            item.unit ||
            item.UOM ||
            item.uom ||
            item.UnitOfMeasure ||
            item.unit_of_measure ||
            "";
        }
        if (!unit) {
          unit = "Units";
        }
        const current = map.get(unit) || 0;
        map.set(unit, current + qty);
      }

      const colorThemes = [
        { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" }, // Blue
        { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" }, // Green
        { bg: "#fffbe6", color: "#b45309", border: "#fde68a" }, // Amber/Yellow
        { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" }, // Purple
        { bg: "#f0fdfa", color: "#0f766e", border: "#99f6e4" }, // Teal
        { bg: "#fff1f2", color: "#be123c", border: "#fecdd3" }, // Rose
        { bg: "#eef2ff", color: "#4338ca", border: "#c7d2fe" } // Indigo
      ];

      const result = [];
      let index = 0;
      map.forEach((totalQty, unitName) => {
        const theme = colorThemes[index % colorThemes.length];
        result.push({
          unit: unitName,
          totalQty: totalQty,
          formattedQty: totalQty.toLocaleString(),
          theme: theme
        });
        index++;
      });

      return result;
    },
    filteredRecords() {
      if (!Array.isArray(this.records)) return [];

      let list = this.records.filter(item => {
        if (!item || typeof item !== "object") return false;

        // 1. Expiration Date (From)
        if (this.fromDateExpiry) {
          const expiryStr = this.getDateExpiry(item);
          if (expiryStr) {
            const expiryDate = moment(expiryStr);
            const from = moment(this.fromDateExpiry).startOf("day");
            if (expiryDate.isValid() && expiryDate.isBefore(from)) {
              return false;
            }
          }
        }

        // 2. Expiration Date (To)
        if (this.toDateExpiry) {
          const expiryStr = this.getDateExpiry(item);
          if (expiryStr) {
            const expiryDate = moment(expiryStr);
            const to = moment(this.toDateExpiry).endOf("day");
            if (expiryDate.isValid() && expiryDate.isAfter(to)) {
              return false;
            }
          }
        }

        return true;
      });

      if (this.sortKey) {
        const key = this.sortKey;
        const dir = this.sortDirection === "asc" ? 1 : -1;

        list.sort((a, b) => {
          let valA, valB;
          if (key === "stock_id") {
            valA = this.getStockId(a);
            valB = this.getStockId(b);
          } else if (key === "qty") {
            valA = Number(this.getQty(a)) || 0;
            valB = Number(this.getQty(b)) || 0;
          } else if (key === "unit") {
            valA = this.getUnit(a);
            valB = this.getUnit(b);
          } else if (key === "date_received") {
            const rawA = this.getDateReceived(a);
            const rawB = this.getDateReceived(b);
            valA = rawA ? moment(rawA).valueOf() : 0;
            valB = rawB ? moment(rawB).valueOf() : 0;
          } else if (key === "date_expiry") {
            const rawA = this.getDateExpiry(a);
            const rawB = this.getDateExpiry(b);
            valA = rawA ? moment(rawA).valueOf() : 0;
            valB = rawB ? moment(rawB).valueOf() : 0;
          } else if (key === "facility_zone") {
            valA = this.getFacilityZone(a);
            valB = this.getFacilityZone(b);
          } else if (key === "location" || key === "slot_bay" || key === "slot") {
            valA = this.getLocation(a);
            valB = this.getLocation(b);
          } else if (key === "batch" || key === "batch_consignment") {
            valA = this.getBatch(a);
            valB = this.getBatch(b);
          } else {
            valA = a ? a[key] : "";
            valB = b ? b[key] : "";
          }

          if (valA === valB) return 0;
          if (valA === null || valA === undefined || valA === "N/A" || valA === "") return 1;
          if (valB === null || valB === undefined || valB === "N/A" || valB === "") return -1;

          if (typeof valA === "string" && typeof valB === "string") {
            return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: "base" }) * dir;
          }

          return (valA < valB ? -1 : 1) * dir;
        });
      }

      return list;
    }
  },
  mounted() {
    this.fetchStockDetails();
    if (this.$eventBus) {
      this.$eventBus.$on("title", this.handleHeaderNavigation);
      this.$eventBus.$on("breadcrums", this.handleHeaderNavigation);
    }
  },
  beforeDestroy() {
    if (this.$eventBus) {
      this.$eventBus.$off("title", this.handleHeaderNavigation);
      this.$eventBus.$off("breadcrums", this.handleHeaderNavigation);
    }
  },
  methods: {
    async fetchStockDetails() {
      if (!this.currentSku) {
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const res = await axios.get(
          `/v1/admin/stockdetail/${encodeURIComponent(this.currentSku)}`
        );
        let raw = res.data;
        if (raw && raw.data !== undefined) {
          raw = raw.data;
        }

        if (Array.isArray(raw)) {
          this.records = raw;
        } else if (raw && typeof raw === "object") {
          this.records = raw.items || raw.records || raw.data || [raw];
          if (raw.item_description) {
            this.fetchedItemDescription = raw.item_description;
          }
        } else {
          this.records = [];
        }
      } catch (err) {
        console.error("Error fetching stock detail:", err);
        this.records = [];
      } finally {
        this.loading = false;
      }
    },
    handleHeaderNavigation() {
      if (this.isGoingBack) return;
      if (
        this.$router &&
        this.$route &&
        (this.$route.name === "SkuDetail" ||
          this.$route.name === "DashboardSkuDetail" ||
          (this.$route.path && this.$route.path.includes("/sku/")))
      ) {
        this.$router.push("/dashboard");
      }
    },
    goBack() {
      this.isGoingBack = true;
      if (this.$eventBus) {
        this.$eventBus.$emit("title", { value: 14 });
      }
      if (this.$router) {
        if (window.history && window.history.length > 1) {
          this.$router.go(-1);
        } else {
          this.$router.push("/dashboard");
        }
      }
    },
    applyFilters() {
      // Dynamic computed filtering
    },
    clearFilters() {
      this.fromDateExpiry = "";
      this.toDateExpiry = "";
    },
    getStockId(item) {
      if (!item) return "N/A";
      return item.StockId || item.stock_id || item.StockID || item.id || "N/A";
    },
    getQty(item) {
      if (!item) return 0;
      return item.Qty !== undefined
        ? item.Qty
        : item.quantity !== undefined
        ? item.quantity
        : item.QtyAvailable !== undefined
        ? item.QtyAvailable
        : item.qty || 0;
    },
    getQtyDisplay(item) {
      const q = this.getQty(item);
      let unit = this.getUnit(item);
      return `${q} ${unit}`;
    },
    getUnit(item) {
      if (!item) return "Units";
      let unit =
        item.Unit ||
        item.unit ||
        item.UOM ||
        item.uom ||
        item.UnitOfMeasure ||
        item.unit_of_measure ||
        "";
      if (!unit) {
        const q = this.getQty(item);
        unit = parseInt(q, 10) === 1 ? "Unit" : "Units";
      }
      return unit;
    },
    getDateReceived(item) {
      if (!item) return null;
      return (
        item.DateReceipt ||
        item.date_receipt ||
        item.DateReceived ||
        item.received_date ||
        item.created_at ||
        null
      );
    },
    getDateExpiry(item) {
      if (!item) return null;
      return (
        item.DateExpiry ||
        item.date_expiry ||
        item.ExpirationDate ||
        item.expiry_date ||
        null
      );
    },
    getFacilityZone(item) {
      if (!item) return "N/A";
      if (item.Facility && item.Zone) return `${item.Facility} / ${item.Zone}`;
      return (
        item.Facility || item.facility || item.Zone || item.location || "N/A"
      );
    },
    getLocation(item) {
      if (!item) return "N/A";
      return item.Slot || item.slot || item.location_code || "N/A";
    },
    getSlotBay(item) {
      return this.getLocation(item);
    },
    getBatch(item) {
      if (!item) return "N/A";
      return item.Batch || item.batch || item.batch_no || "N/A";
    },
    getStatus(item) {
      if (!item) return "Available";
      return item.Status || item.status || item.StockStatus || "Available";
    },
    getBatchConsignment(item) {
      return this.getBatch(item);
    },
    formatDate(val) {
      if (!val || val === "N/A") return "N/A";
      try {
        const m = moment(val);
        if (!m.isValid()) return String(val);
        return m.format("MM/DD/YYYY");
      } catch (e) {
        return String(val);
      }
    },
    isExpiringSoon(val) {
      if (!val) return false;
      try {
        const m = moment(val);
        if (!m.isValid()) return false;
        return m.diff(moment(), "days") <= 30;
      } catch (e) {
        return false;
      }
    },
    getStatusClass(status) {
      const s = String(status || "").toLowerCase();
      if (s.includes("hold") || s.includes("lock")) return "status-hold";
      if (s.includes("alloc") || s.includes("resv")) return "status-allocated";
      return "status-available";
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDirection = "asc";
      }
    },
    sortedBy(key) {
      this.sortBy(key);
    },
    getSortIcon(key) {
      if (this.sortKey !== key) return "↕️";
      return this.sortDirection === "asc" ? "⬆️" : "⬇️";
    }
  }
};
</script>

<style scoped lang="scss">
.sku-detail-page {
  background-color: #e6ecf0 !important;
  min-height: 100vh;
  padding-top: 110px !important;
  margin-top: 0 !important;
  padding-bottom: 60px;
  width: 100vw;
  max-width: 100%;
}

.sku-detail-container {
  max-width: 1190px;
  margin: 0 auto;
  padding: 0 20px;
  font-family: TTNormsMedium, -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, sans-serif;
  color: #1e293b;
}

.breadcrumb-top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Card Common */
.card-box {
  background: #ffffff;
  border-radius: 12px;
  padding: 22px 26px;
  border: 1px solid #dce4ec;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
}

/* Card 1: Overview Header */
.overview-card {
  padding: 16px 22px;

  .subtitle-row {
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid #f1f5f9;
  }

  .card-subtitle-gray {
    font-size: 11px;
    font-weight: 700;
    color: #8a94a6;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    display: inline-block;
  }

  .controls-row {
    margin-top: 0;
  }

  /* Combined Highlighted SKU & Product Name Card */
  .combined-sku-item-card {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%);
    border: 1.5px solid #005eb8;
    border-radius: 10px;
    padding: 7px 18px;
    box-shadow: 0 4px 12px rgba(0, 94, 184, 0.1);
    transition: all 0.25s ease;

    &:hover {
      border-color: #00468b;
      box-shadow: 0 6px 16px rgba(0, 94, 184, 0.16);
      transform: translateY(-1px);
    }

    .item-card-icon {
      font-size: 18px;
    }

    .combined-item-desc {
      font-size: 17px;
      font-weight: 800;
      color: #005eb8;
      letter-spacing: -0.2px;
    }

    .sku-badge-pill {
      display: inline-block;
      color: #64748b;
      font-size: 14px;
      font-weight: 700;
      padding: 3px 10px;
      border-radius: 6px;
      letter-spacing: 0.5px;
    }
  }

  /* Prominent Total Quantity Badge */
  .prominent-total-qty-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, #1e40af 0%, #005eb8 100%);
    color: #ffffff;
    padding: 8px 18px;
    border-radius: 10px;
    box-shadow: 0 4px 6px -1px rgba(0, 94, 184, 0.25);

    .qty-badge-label {
      font-size: 12px;
      font-weight: 600;
      color: #e0f2fe;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .qty-badge-val {
      font-size: 14px;
      font-weight: 800;
      color: #ffffff;
    }
  }

  /* Compact Date Filter Bar */
  .compact-date-filter {
    background: #f8fafc;
    border: 1px solid #cbd5e1;
    padding: 6px 14px;
    border-radius: 10px;

    .filter-icon-label {
      font-size: 12px;
      font-weight: 700;
      color: #334155;
    }

    .range-arrow {
      color: #94a3b8;
      font-weight: 700;
      font-size: 13px;
    }

    .date-input-chip {
      display: inline-flex;
      align-items: center;
      background: #ffffff;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      padding: 3px 8px;
      gap: 6px;

      .chip-label {
        font-size: 11px;
        font-weight: 600;
        color: #64748b;
      }

      .compact-date-input {
        border: none;
        outline: none;
        font-size: 12px;
        color: #1e293b;
        background: transparent;
        font-family: inherit;
        cursor: pointer;
      }
    }

    .clear-chip-btn {
      background: #fee2e2;
      color: #dc2626;
      border: 1px solid #fca5a5;
      border-radius: 6px;
      padding: 3px 8px;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #dc2626;
        color: #ffffff;
      }
    }
  }

  .refresh-data-btn {
    background: #ffffff;
    color: #334155;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover {
      background: #f8fafc;
      border-color: #94a3b8;
    }

    .refresh-icon.spinning {
      display: inline-block;
      animation: spin 1s linear infinite;
    }
  }
}

.back-inventory-btn {
  background: #005eb8;
  color: #ffffff;
  border: 1px solid #005eb8;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #00468b;
    border-color: #00468b;
  }
}

/* Product Quantity Breakdown Table Card */
.table-card {
  padding: 0;
  overflow: hidden;

  .table-header-row {
    padding: 16px 20px;
    border-bottom: 1px solid #f1f5f9;
  }

  .table-title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }

  .entries-badge {
    background: #e0f2fe;
    color: #0284c7;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 12px;
    border-radius: 14px;
  }
}

.table-responsive {
  width: 100%;
  overflow-x: hidden;
}

/* Modern Table (Styled 100% identically to InventoryItems.vue modern-table) */
.modern-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;
  table-layout: fixed;

  thead tr {
    background: #005eb8;
    color: #ffffff;
  }

  th {
    padding: 10px 14px;
    font-weight: 600;
    color: #ffffff;
    font-size: 12px;
    line-height: 1.3;
    word-wrap: break-word;

    &.sortable {
      cursor: pointer;
      user-select: none;
      transition: background 0.15s ease;

      &:hover {
        background: #004c97;
      }
    }
  }

  .sort-icon {
    font-size: 11px;
    margin-left: 4px;
    opacity: 0.8;
    display: inline-block;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    word-break: break-word;
    font-size: 13px;
    color: #1e293b;
  }

  .table-row {
    transition: background-color 0.15s ease;

    &:nth-child(even) {
      background-color: #ffffff;
    }
    &:nth-child(odd) {
      background-color: #f8fafc;
    }

    &:hover {
      background-color: #f0f7ff !important;
    }
  }

  .clickable-sku-code {
    background: #f1f5f9;
    color: #005eb8;
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: 700;
    font-size: 12px;
    font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
    display: inline-block;
  }

  /* Quantity Badge Styling */
  .qty-item-badge {
    display: inline-block;
    background: #eff6ff;
    color: #005eb8;
    border: 1px solid #bfdbfe;
    padding: 4px 12px;
    border-radius: 6px;
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.3px;
    box-shadow: 0 1px 2px rgba(0, 94, 184, 0.08);
  }

  /* Unit Badge Styling */
  .unit-tag {
    display: inline-block;
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
    padding: 3px 10px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.2px;
  }

  .date-cell {
    font-size: 13px;
    color: #334155;
    font-weight: 500;
  }

  .location-tag {
    background: #f1f5f9;
    color: #334155;
    padding: 3px 8px;
    border-radius: 4px;
    font-weight: 500;
    font-size: 12px;
    display: inline-block;

    &.sub-loc {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
    }
  }

  .no-records-cell {
    padding: 30px;
    color: #64748b;
    font-size: 14px;
  }
}

/* Unit Quantity Breakdown Summary */
.unit-breakdown-summary {
  border-top: 1px solid #e2e8f0;
  padding: 16px 20px 20px 20px;
  background: #f8fafc;

  .unit-summary-title {
    font-size: 14px;
    font-weight: 700;
    color: #334155;
    margin: 0 0 12px 0;
  }

  .unit-total-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    border: 1px solid;
    font-size: 13px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

    .unit-chip-name {
      font-weight: 600;
    }

    .unit-chip-val {
      font-weight: 800;
      font-size: 14px;
    }
  }
}

/* Utility Helpers */
.flex-between {
  display: flex;
  justify-content: space-between;
}
.flex-align-center {
  display: flex;
  align-items: center;
}
.flex-wrap {
  flex-wrap: wrap;
}
.gap-8 {
  gap: 8px;
}
.gap-10 {
  gap: 10px;
}
.gap-12 {
  gap: 12px;
}
.gap-16 {
  gap: 16px;
}
.mb-12 {
  margin-bottom: 12px;
}
.mb-16 {
  margin-bottom: 16px;
}
.mb-20 {
  margin-bottom: 20px;
}
.mt-20 {
  margin-top: 20px;
}
.font-semibold {
  font-weight: 600;
}
.font-bold {
  font-weight: 700;
}
.font-medium {
  font-weight: 500;
}
.text-center {
  text-align: center;
}
.text-blue {
  color: #2563eb;
}
.text-expiry {
  color: #dc2626;
  font-weight: 700;
}

.loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
  color: #64748b;

  .spinner-img {
    width: 44px;
    height: 44px;
    margin-bottom: 14px;

    &.spinning {
      animation: spin 1s linear infinite;
    }
  }

  .loading-text {
    font-size: 14px;
    color: #475569;
    font-weight: 500;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
